import { useCallback, useEffect, useState } from 'react';
import { LoaderCircle, Search } from 'lucide-react';
import { useAtomValue } from 'jotai';
import { uiStateAtomFiltersModalIsOpened } from '@rocco/ui/filter-sort/states/ui-states-filters-modal';
import { ReactComponent as CompassIcon } from '@rocco/icons/svg/compass.svg';
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@rocco/components/popover';
import { Input } from '@rocco/components/input';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { LocationPlace } from '@rocco/ui/location/types/location-place';
import type { FilterModalElementProps } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import type { FilterConfigModalItemDefault } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { LocationFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/location-filter-state';
import { formatLocation } from '@rocco/utils/format-location';

export const FiltersModalLocation = (
    props: FilterModalElementProps<
        FilterConfigModalItemDefault,
        LocationFilterState
    >,
) => {
    const { filterState, onFilterStateUpdate } = props;
    const { searchFilters } = useFiltersContext();
    const { locationService } = searchFilters;

    const filterOpened = useAtomValue(uiStateAtomFiltersModalIsOpened);

    const [selectedLocation, setSelectedLocation] = useState(
        filterState.selectedLocation ??
            locationService.currentLocation.location,
    );

    const [isEditing, setEditing] = useState(false);
    const [suggestions, setSuggestions] = useState<LocationPlace[]>([]);
    const [isSuggesting, setSuggesting] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [isSettingCurrentLocation, allowToSetCurrentLocation] =
        useState(false);

    const handleKeywordChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        if (event.currentTarget) {
            const value = event.currentTarget.value;

            setKeyword(value);

            if (value.trim().length > 2) {
                locationService.keywordLocations.onKeywordLocationsFetch(
                    value,
                    10,
                );
            }
        }
    };

    const resetLocationInput = useCallback(() => {
        locationService.keywordLocations.onKeywordResetPlaces();

        setKeyword('');
        setEditing(false);
        setSuggesting(false);
        setSuggestions([]);
        allowToSetCurrentLocation(false);
    }, [locationService.keywordLocations]);

    const handleSuggestionSelect = (location: LocationPlace) => {
        setSelectedLocation(location);
        resetLocationInput();
    };

    // update suggestions when keyword locations change
    useEffect(() => {
        if (locationService.keywordLocations.locations) {
            setSuggestions(locationService.keywordLocations.locations);
            setSuggesting(true);
            allowToSetCurrentLocation(false);
        }
    }, [locationService.keywordLocations.locations]);

    // set current location
    useEffect(() => {
        if (
            isSettingCurrentLocation &&
            locationService.currentLocation.location
        ) {
            setSelectedLocation(locationService.currentLocation.location);
            allowToSetCurrentLocation(false);
        }
    }, [locationService.currentLocation.location, isSettingCurrentLocation]);

    // update aggregations
    useEffect(() => {
        onFilterStateUpdate({
            selectedLocation: selectedLocation ?? null,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation]);

    // listen for filter modal close
    // reset suggestions, editing and suggesting
    useEffect(() => {
        if (!filterOpened) {
            resetLocationInput();
        }
    }, [filterOpened, resetLocationInput]);

    return (
        <Popover
            open={isSuggesting && suggestions.length > 0}
            onOpenChange={setSuggesting}
        >
            <PopoverAnchor asChild>
                <div className="w-full">
                    <Input
                        color="outline"
                        text="small"
                        shape="rounded"
                        placeholder="Postcode, suburb or region"
                        role="combobox"
                        value={
                            isEditing
                                ? keyword
                                : formatLocation(selectedLocation)
                        }
                        showClearButton={true}
                        postfix={<Search className="size-4.5" />}
                        onChange={handleKeywordChange}
                        onFocus={() => setEditing(true)}
                        onBlur={() => {
                            setEditing(false);
                            setKeyword('');
                        }}
                        onClear={() => {
                            setKeyword('');
                            setSuggestions([]);
                            setSelectedLocation(null);
                        }}
                    />
                </div>
            </PopoverAnchor>

            <div className="mt-4 flex items-center gap-2">
                <div
                    className="flex cursor-pointer gap-2"
                    onClick={() => {
                        locationService.currentLocation.onCurrentLocationFetch();
                        allowToSetCurrentLocation(true);
                    }}
                >
                    <CompassIcon className="size-4.5" />
                    <span className="underline underline-offset-2">
                        Use my current location
                    </span>
                </div>

                {locationService.currentLocation.isLoading && (
                    <div className="flex-centered size-4 opacity-50">
                        <LoaderCircle className="size-4 animate-spin" />
                    </div>
                )}
            </div>

            <PopoverContent
                align="start"
                paddings="verticalSmall"
                role="menu"
                onOpenAutoFocus={e => e.preventDefault()}
            >
                {suggestions.map((suggestion, index) => (
                    <div
                        key={index}
                        className="cursor-pointer px-4 py-2 hover:bg-gray-100"
                        role="option"
                        onClick={() => handleSuggestionSelect(suggestion)}
                    >
                        {formatLocation(suggestion)}
                    </div>
                ))}
            </PopoverContent>
        </Popover>
    );
};
